import { useState } from 'react';
import { X, ShoppingBag, Receipt, User } from 'lucide-react';
import { Order } from '../../services/orders.service';
import { PaymentModal } from './PaymentModal';
import { ordersService } from '../../services/orders.service';
import { toast } from 'sonner';

interface TableOrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
  tableName: string;
  orders: Order[];
  onOrdersUpdate: () => void;
}

const statusConfig = {
  PENDING: { label: 'New Order', color: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20' },
  CONFIRMED: { label: 'Accepted', color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20' },
  PREPARING: { label: 'Wait time (15-40 min)', color: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20' },
  READY: { label: 'Ready', color: 'text-green-600 bg-green-50 dark:bg-green-900/20' },
  DELIVERED: { label: 'Delivered', color: 'text-teal-600 bg-teal-50 dark:bg-teal-900/20' },
  CLOSED: { label: 'Closed', color: 'text-gray-600 bg-gray-50 dark:bg-gray-900/20' },
  CANCELLED: { label: 'Cancelled', color: 'text-red-600 bg-red-50 dark:bg-red-900/20' },
  REJECTED: { label: 'Rejected', color: 'text-red-600 bg-red-50 dark:bg-red-900/20' },
  REFUNDED: { label: 'Refunded', color: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20' },
};

export function TableOrdersModal({ isOpen, onClose, tableName, orders, onOrdersUpdate }: TableOrdersModalProps) {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  if (!isOpen) return null;

  // Filter out only closed, cancelled, rejected, and refunded orders
  // DELIVERED orders should still be visible and allow payment
  const activeOrders = orders.filter(
    order => !['CLOSED', 'CANCELLED', 'REJECTED', 'REFUNDED'].includes(order.status)
  );

  // Calculate total from all active orders
  const totalAmount = activeOrders.reduce((sum, order) => sum + Number(order.total || 0), 0);

  const handleCheckout = () => {
    if (activeOrders.length === 0) {
      toast.error('No active orders to checkout');
      return;
    }
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = async (paymentData: {
    method: 'CASH' | 'MPESA' | 'CARD';
    amount: number;
    discount: number;
  }) => {
    setProcessingPayment(true);
    
    try {
      // Update all active orders to CLOSED status (payment completed)
      const updatePromises = activeOrders.map(order =>
        ordersService.updateOrder(order.id, {
          status: 'CLOSED',
          notes: `${order.notes || ''}\nPayment: ${paymentData.method} - Amount: KSh ${paymentData.amount.toFixed(2)}${paymentData.discount > 0 ? ` (${paymentData.discount}% discount)` : ''}`.trim(),
        })
      );

      await Promise.all(updatePromises);

      toast.success(`Payment completed! ${activeOrders.length} order(s) closed.`);
      
      // Refresh orders
      onOrdersUpdate();
      
      // Close modals
      setShowPaymentModal(false);
      onClose();
    } catch (error) {
      console.error('Failed to process payment:', error);
      toast.error('Failed to process payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{tableName}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {activeOrders.length} active {activeOrders.length === 1 ? 'order' : 'orders'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <Receipt className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  No Active Orders
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  This table has no pending orders.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {activeOrders.map((order) => {
                  const config = statusConfig[order.status];
                  
                  return (
                    <div
                      key={order.id}
                      className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
                    >
                      {/* Order Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {order.orderNumber}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-lg font-medium ${config.color}`}>
                            {config.label}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          KSh {Number(order.total || 0).toFixed(2)}
                        </span>
                      </div>

                      {/* Order Items */}
                      {order.items && order.items.length > 0 && (
                        <div className="space-y-2">
                          {order.items.map((item) => {
                            // Handle both flat and nested product data
                            const productName = item.productName || (item as any).product?.name || 'Unknown Item';
                            const productSku = item.productSku || (item as any).product?.sku || '';
                            
                            return (
                              <div
                                key={item.id}
                                className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="inline-flex items-center justify-center w-6 h-6 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-bold rounded">
                                        {item.quantity}
                                      </span>
                                      <span className="font-medium text-gray-900 dark:text-gray-100">
                                        {productName}
                                      </span>
                                    </div>
                                    <div className="ml-8 space-y-1">
                                      <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                        <span>KSh {Number(item.price || 0).toFixed(2)} each</span>
                                        {productSku && (
                                          <span className="text-gray-400">• {productSku}</span>
                                        )}
                                        {item.discount && item.discount > 0 && (
                                          <span className="text-green-600 dark:text-green-400 font-medium">
                                            • {item.discount}% off
                                          </span>
                                        )}
                                      </div>
                                      {item.notes && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                          Note: {item.notes}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="ml-4 text-right">
                                    <div className="font-semibold text-gray-900 dark:text-white">
                                      KSh {Number(item.total || 0).toFixed(2)}
                                    </div>
                                    {item.discount && item.discount > 0 && (
                                      <div className="text-xs text-gray-500 dark:text-gray-400 line-through">
                                        KSh {(Number(item.price || 0) * item.quantity).toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Waiter/Server Information */}
                      {order.user && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Served by</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {order.user.firstName} {order.user.lastName}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Order Time */}
                      <div className={`mt-3 pt-3 ${!order.user ? 'border-t border-gray-200 dark:border-gray-700' : ''}`}>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(order.createdAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer - Checkout Section */}
          {activeOrders.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Total Amount
                </span>
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  KSh {totalAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </span>
              </div>
              
              <button
                onClick={handleCheckout}
                disabled={processingPayment}
                className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingPayment ? 'Processing...' : 'Proceed to Payment'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        orderTotal={totalAmount}
        onPaymentComplete={handlePaymentComplete}
      />
    </>
  );
}